/**
 * Shell-level breadcrumb resolver (Story #5351 W1-T17, Screen Contract G02:
 * "Persistent page opening block above tabs and primary content" on "All
 * entity and work routes").
 *
 * Three pages already hand-roll their own page-level <Breadcrumb> (built in
 * W1-T4/T12: IngestionJobDetail.tsx, IngestionWizard.tsx,
 * pages/home/AttentionDetail.tsx) — this resolver deliberately EXCLUDES
 * those routes so Layout never double-renders a breadcrumb above one that's
 * already there. Every OTHER entity/work detail route in App.tsx's route
 * table had ZERO breadcrumb before this task (verified: only the three
 * files above import ui/Breadcrumb) — this resolver closes that gap for
 * the detail routes below. It does not attempt every one of the ~40 routes
 * in the app; PARTIAL vs. the contract's "all entity and work routes" is
 * disclosed in the task's evidence rather than silently claimed as full.
 */
import type { BreadcrumbItem } from '../ui/Breadcrumb';

type Resolver = (params: Record<string, string>) => BreadcrumbItem[];

interface RoutePattern {
  /** path-to-regexp-lite: `:param` segments become named capture groups. */
  pattern: string;
  resolve: Resolver;
}

function compile(pattern: string): RegExp {
  const escaped = pattern
    .split('/')
    .map((seg) => (seg.startsWith(':') ? `(?<${seg.slice(1)}>[^/]+)` : seg))
    .join('/');
  return new RegExp(`^${escaped}$`);
}

const ROUTES: RoutePattern[] = [
  {
    pattern: '/environments/:env',
    resolve: (p) => [
      { label: 'Environments', to: '/environments' },
      { label: decodeURIComponent(p.env) },
    ],
  },
  {
    pattern: '/organisations/:id',
    resolve: (p) => [
      { label: 'Organisations', to: '/organisations' },
      { label: decodeURIComponent(p.id) },
    ],
  },
  {
    pattern: '/developer-vms/:vmId',
    resolve: (p) => [
      { label: 'Developer VMs', to: '/developer-vms' },
      { label: decodeURIComponent(p.vmId) },
    ],
  },
  {
    pattern: '/services/:componentId',
    resolve: (p) => [
      { label: 'Platform Services', to: '/services' },
      { label: decodeURIComponent(p.componentId) },
    ],
  },
  {
    pattern: '/portals/:portalId/settings',
    resolve: (p) => [
      { label: 'Portals', to: '/portals' },
      { label: decodeURIComponent(p.portalId) },
      { label: 'Settings' },
    ],
  },
  {
    pattern: '/contacts/people/:id',
    resolve: (p) => [
      { label: 'CRM Workspace', to: '/crm' },
      { label: 'Contacts', to: '/contacts/people' },
      { label: decodeURIComponent(p.id) },
    ],
  },
  {
    pattern: '/datastores/:name/browse',
    resolve: (p) => [
      { label: 'Data Stores', to: '/datastores' },
      { label: decodeURIComponent(p.name) },
    ],
  },
];

const COMPILED = ROUTES.map((r) => ({ regex: compile(r.pattern), resolve: r.resolve }));

/** Returns the breadcrumb for a known entity/work detail route, or null when none applies (list pages, routes with their own hand-rolled breadcrumb, or routes not yet covered). */
export function resolveShellBreadcrumb(pathname: string): BreadcrumbItem[] | null {
  for (const { regex, resolve } of COMPILED) {
    const match = regex.exec(pathname);
    if (match?.groups) return resolve(match.groups);
  }
  return null;
}
